"use client";

import { useState, useEffect, useMemo } from "react";
import { ChevronLeft, User, Clock } from "lucide-react";
import { addDays } from "date-fns";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { LanguageSelector } from "./LanguageSelector";
import { BookingCalendar } from "./BookingCalendar";
import { GroupSizeExceedsModal } from "./GroupSizeExceedsModal";
import { useTranslation } from "react-i18next";
import { useRouter, useSearchParams } from "next/navigation";
import { useCalendarAvailability } from "@/hooks/useCalendarAvailability";
import { useVenueDetails } from "@/hooks/useVenueDetails";
import { Button } from "@/components/ui/button";

const timeSlots = [
  { id: "morning", time: "10:00–15:00", start: "10:00", end: "15:00" },
  { id: "afternoon", time: "15:00–19:00", start: "15:00", end: "19:00" },
  { id: "evening", time: "19:00–23:00", start: "19:00", end: "23:00" },
];

interface GroupSectionProps {
  bookingType: "private" | "company";
  venueId: string;
  initialParticipants?: number;
  initialDate?: Date;
  initialTime?: string | null;
  onBackOverride?: () => void;
  onContinue?: (selection: {
    participants: number;
    timeSlotId: string;
    timeSlotStart: string;
    timeSlotEnd: string;
    date: Date;
    dayOfWeek: number;
  }) => void;
  onRequestSupport?: () => void;
  supportEmail?: string | null;
  supportPhone?: string | null;
  venueName?: string;
}

export function GroupSection({
  bookingType,
  venueId,
  initialParticipants,
  initialDate,
  initialTime,
  onBackOverride,
  onContinue,
  onRequestSupport,
  supportEmail,
  supportPhone,
  venueName,
}: GroupSectionProps) {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [groupSize, setGroupSize] = useState<number>(initialParticipants ?? 6);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    initialDate,
  );
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [showExceedsModal, setShowExceedsModal] = useState<boolean>(false);
  
  const lang =
    (searchParams.get("lang") ||
      i18n.resolvedLanguage ||
      i18n.language ||
      "en")
      .split("-")[0]
      .toLowerCase();
  const availabilityEnabled = Boolean(selectedTimeSlot);

  const mapTimeToSlot = (time: string | null | undefined): string | null => {
    if (!time) return null;
    const hour = parseInt(time.split(":")[0] || "0", 10);
    if (hour >= 10 && hour < 15) return "morning";
    if (hour >= 15 && hour < 19) return "afternoon";
    if (hour >= 19 && hour < 23) return "evening";
    return null;
  };

  useEffect(() => {
    if (initialTime) {
      setSelectedTimeSlot(mapTimeToSlot(initialTime));
    }
  }, [initialTime]);

  // Helper function to convert booking type to customer type ID
  const getCustomerTypeId = (type: string): string => {
    return type === "company" ? "2" : "1"; // 1 = private, 2 = company
  };

  // Helper function to convert time slot ID to start/end times
  const getTimeSlotRange = (slotId: string): { start: string; end: string } => {
    const slot = timeSlots.find((item) => item.id === slotId);
    if (slot) {
      return { start: slot.start, end: slot.end };
    }
    return { start: "09:00", end: "23:00" }; // Full day as fallback
  };

  // Get time slot range for calendar availability
  const timeSlotRange = selectedTimeSlot
    ? getTimeSlotRange(selectedTimeSlot)
    : null;

  // Use the calendar availability hook (with participant count and time slot)
  const customerTypeId = getCustomerTypeId(bookingType);
  const {
    getDateAvailability,
    isLoading: isLoadingAvailability,
    error: availabilityError,
  } = useCalendarAvailability(
    venueId,
    customerTypeId,
    groupSize,
    availabilityEnabled ? timeSlotRange?.start : null,
    availabilityEnabled ? timeSlotRange?.end : null,
    undefined,
    availabilityEnabled
  );

  // Use venue details hook
  const {
    minParticipants,
    maxParticipants,
    isLoading: isLoadingVenue,
    languages: venueLanguages,
  } = useVenueDetails(venueId);

  // Initialize group size within the valid range
  useEffect(() => {
    setGroupSize((prevSize) => {
      const desired = initialParticipants ?? prevSize;
      if (desired < minParticipants) {
        return minParticipants;
      } else if (desired > maxParticipants) {
        return maxParticipants;
      }
      return desired;
    });
  }, [minParticipants, maxParticipants, initialParticipants]);

  // Handle navigation back
  const handleBack = () => {
    if (showDatePicker) {
      setShowDatePicker(false);
      setSelectedTimeSlot(null);
      setSelectedDate(undefined);
    } else {
      if (onBackOverride) {
        onBackOverride();
      } else {
        router.push(`/bookings/venues?lang=${lang}&type=${bookingType}`);
      }
    }
  };

  // Set initial date to today when time slot is first selected
  const handleTimeSlotSelect = (slotId: string) => {
    setSelectedTimeSlot(slotId);
    // Don't auto-select today - let user click to select
    // Show date picker screen
    setShowDatePicker(true);
  };

  // Simple date click handler - just triggers navigation!
  const handleDateClick = (date: Date) => {
    // Only proceed if a time slot is selected
    if (selectedTimeSlot) {
      // Set the date and immediately continue
      setSelectedDate(date);
      // Format date in local timezone to avoid timezone shift issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;
      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const timeRange = getTimeSlotRange(selectedTimeSlot);
      const customerTypeId = getCustomerTypeId(bookingType);

      if (onContinue && timeRange) {
        onContinue({
          participants: groupSize,
          timeSlotId: selectedTimeSlot,
          timeSlotStart: timeRange.start,
          timeSlotEnd: timeRange.end,
          date,
          dayOfWeek,
        });
        return;
      }

      // Build URL with all required parameters for the API
      const params = new URLSearchParams({
        lang,
        type: bookingType,
        venueId,
        participants: groupSize.toString(),
        timeSlot: selectedTimeSlot, // Keep this for reference
        date: dateStr, // Keep this for display
        timeSlotStart: timeRange.start,
        timeSlotEnd: timeRange.end,
        dayOfWeek: dayOfWeek.toString(),
        customerTypeId,
      });

      router.push(`/bookings/shows?${params.toString()}`);
    }
  };

  // Show exceeds modal when group size exceeds max
  if (showExceedsModal) {
    return (
      <GroupSizeExceedsModal
        maxGroupSize={maxParticipants}
        venueName={venueName}
        supportEmail={supportEmail || undefined}
        supportPhone={supportPhone || undefined}
        onBack={() => {
          setShowExceedsModal(false);
          setGroupSize(maxParticipants);
        }}
        onRequestSupport={onRequestSupport}
      />
    );
  }

  return (
    <section className="w-full min-h-screen flex flex-col relative">
      {/* Language selector - positioned absolutely in top right */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector availableLanguages={venueLanguages} />
      </div>

      {!showDatePicker ? (
        // Group and Time Selection Screen
        <div className="flex-1 flex items-center text-center justify-center py-8 px-4 md:py-12">
          <div className="w-full max-w-3xl mx-auto space-y-6">
            <button
              onClick={handleBack}
              className="inline-flex text-center items-center gap-2 text-sm hover:text-primary bg-transparent border-none p-0 outline-none focus:outline-none"
              suppressHydrationWarning
            >
              <ChevronLeft className="h-4 w-4" />
              <span suppressHydrationWarning>{t("booking.group.back")}</span>
            </button>

            <div className="space-y-2 text-center">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold" suppressHydrationWarning>
                {t("booking.group.title", {
                  type:
                    bookingType === "private"
                      ? t("booking.types.private.title")
                      : t("booking.types.company.title"),
                })}
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground" suppressHydrationWarning>
                {t("booking.group.subtitle")}
              </p>
            </div>

            <div className="space-y-6">
              {/* Group Size Selection */}
              <div className="space-y-3">
                <Label className="flex items-center justify-center gap-2 text-base">
                  <User className="h-5 w-5" />
                  <span suppressHydrationWarning>{t("booking.group.participants.label")}</span>
                </Label>

                {isLoadingVenue ? (
                  <div className="h-2 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <Slider
                    min={minParticipants}
                    max={maxParticipants + 1}
                    step={1}
                    value={[groupSize]}
                    onValueChange={([value]) => {
                      setGroupSize(value);
                      if (value > maxParticipants) {
                        setShowExceedsModal(true);
                      }
                    }}
                    className="w-full"
                  />
                )}

                <div className="space-y-1 text-center">
                  <p className="text-sm font-medium" suppressHydrationWarning>
                    {t("booking.group.participants.count", {
                      count: groupSize,
                    })}
                  </p>
                  {isLoadingVenue ? (
                    <div className="h-4 bg-gray-200 rounded animate-pulse mx-auto w-48" />
                  ) : (
                    <p className="text-sm text-muted-foreground" suppressHydrationWarning>
                      {t("booking.group.participants.description", {
                        min: Math.max(0, groupSize - 3),
                        max: groupSize + 3,
                      })}
                    </p>
                  )}
                </div>
              </div>

              {onRequestSupport &&
                !isLoadingVenue &&
                groupSize >= maxParticipants && (
                  <div className="text-center">
                    <Button variant="outline" onClick={onRequestSupport}>
                      <span suppressHydrationWarning>{t("booking.group.contactSupport")}</span>
                    </Button>
                  </div>
                )}

              {/* Time Slot Selection */}
              <div className="space-y-3">
                <Label className="flex items-center justify-center gap-2 text-base">
                  <Clock className="h-5 w-5" />
                  <span suppressHydrationWarning>{t("booking.group.timeSlot.label")}</span>
                </Label>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {timeSlots.map((slot) => (
                    <Card
                      key={slot.id}
                      className={cn(
                        "cursor-pointer transition-all duration-300",
                        "hover:scale-[1.02] hover:shadow-md",
                        "focus:ring-2 focus:ring-offset-2 focus:ring-primary",
                        "active:scale-[0.98]",
                        selectedTimeSlot === slot.id && "ring-2 ring-primary",
                      )}
                      onClick={() => handleTimeSlotSelect(slot.id)}
                    >
                      <CardHeader className="text-center py-4 px-3">
                        <CardTitle className="text-base sm:text-lg">
                          {slot.time}
                        </CardTitle>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Date Picker Screen
        <div className="flex-1 flex items-center justify-center py-8 px-4 md:py-12">
          <div className="w-full max-w-3xl mx-auto space-y-6">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-sm hover:text-primary bg-transparent border-none p-0 outline-none focus:outline-none"
            >
              <ChevronLeft className="h-4 w-4" />
              {t("booking.group.back")}
            </button>

            <div className="space-y-2 text-center">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">
                {t("booking.group.date.title")}
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                {t("booking.group.date.subtitle")}
              </p>
            </div>

            <div className="space-y-4">
              {isLoadingAvailability ? (
                <div className="flex justify-center items-center min-h-[300px]">
                  <div className="text-center space-y-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        "booking.group.date.loading",
                        "Loading availability...",
                      )}
                    </p>
                  </div>
                </div>
              ) : availabilityError ? (
                <div className="flex justify-center items-center min-h-[300px]">
                  <div className="text-center space-y-2">
                    <p className="text-sm text-red-600">{availabilityError}</p>
                    <button
                      onClick={() => window.location.reload()}
                      className="text-sm text-primary hover:text-primary/80 underline"
                    >
                      {t("booking.group.date.retry", "Try again")}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center">
                  <div className="max-w-sm w-full rounded-md border p-4">
                    <BookingCalendar
                      mode="single"
                      selected={selectedDate}
                      className="w-full"
                      getDateAvailability={getDateAvailability}
                      onDateClick={handleDateClick}
                      startMonth={new Date()}
                      defaultMonth={selectedDate || new Date()}
                      endMonth={addDays(new Date(), 365)}
                      disabled={{ before: new Date() }}
                      lang={lang}
                    />
                  </div>
                </div>
              )}

              {!isLoadingAvailability && !availabilityError && (
                <>
                  <div className="flex justify-center space-x-6 text-xs">
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-0.5 bg-green-500 rounded-full"></div>
                      <span>
                        {t("booking.group.date.legend.available", "Available")}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-0.5 bg-yellow-500 rounded-full"></div>
                      <span>
                        {t(
                          "booking.group.date.legend.partial",
                          "Partially booked",
                        )}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-0.5 bg-red-500 rounded-full"></div>
                      <span>
                        {t(
                          "booking.group.date.legend.unavailable",
                          "Unavailable",
                        )}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
